import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreatorSearch } from '../components/useCreatorSearch';
import { useSiteLang as useLang } from '../siteLang';
import { useTranslation } from '../hooks/translations';
import { useLoginModal } from '../LoginModalContext';
import supabase from '../supabase';

// Menu plein écran façon catalogue — remplace l'ancienne barre desktop
// (liens + recherche + admin) et l'ancien petit menu déroulant mobile.
// Sur toutes les tailles d'écran, la barre visible ne montre plus que
// le logo + le mot "Menu" ; tout le reste vit dans l'overlay.
//
// Vibe typographique unique : Helvetica Neue léger (300), pas de pilules
// ni de bordures de bouton — tout est du texte nu, et c'est le survol
// (soulignement qui se dessine + légère variation d'opacité) qui dit
// "c'est cliquable". La classe .wlink ci-dessous porte cet effet ; comme
// ce <style> est toujours monté (Navbar est présent sur presque toutes
// les pages), .wlink est réutilisable ailleurs (ex. le bouton de la
// campagne sur Home.js) sans dupliquer le CSS.

const OV_BG = '#0e1013';
const OV_TEXT = '#f4efe4';
const OV_MUTED = 'rgba(244,239,228,0.45)';
const OV_MUTED_2 = 'rgba(244,239,228,0.3)';
const OV_BORDER = 'rgba(244,239,228,0.14)';

const UI_FONT = 'Helvetica Neue, Arial, sans-serif';

function Navbar({ variant = 'light' }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const { lang, cycleLang } = useLang();
    const t = useTranslation(lang);
    const { openLoginModal } = useLoginModal();

    const { searchQuery, setSearchQuery, searchResults, clearSearch } = useCreatorSearch();

    const [authUser, setAuthUser] = useState(null);

    const loadAuth = async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) { setAuthUser(null); return; }
        const { data: userRow } = await supabase
            .from('users')
            .select('username, role')
            .eq('user_id', sessionData.session.user.id)
            .single();
        setAuthUser(userRow || null);
    };

    useEffect(() => {
        loadAuth();
        const { data: listener } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'SIGNED_IN') loadAuth();
            if (event === 'SIGNED_OUT') setAuthUser(null);
        });
        return () => listener.subscription.unsubscribe();
    }, []);

    // Ferme le menu à chaque changement de route (bouton précédent du navigateur, etc.)
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Bloque le scroll du fond et permet Échap pour fermer, tant que le menu est ouvert
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOpen]);

    const closeMenu = () => {
        setMenuOpen(false);
        clearSearch();
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
        closeMenu();
    };

    const goToSettings = () => {
        navigate('/admin/settings');
        closeMenu();
    };

    const isLight = variant === 'light';
    const color = isLight ? '#111' : '#f4efe4';
    const isAdminPage = location.pathname === '/admin';

    const navLinks = [
        { label: t.nav_chroniques, path: '/chroniques' },
        { label: lang === 'fr' ? 'Campagne' : 'Campaign', path: '/campaigns' },
        { label: lang === 'fr' ? 'Carte postale' : 'Postcard', path: '/postcard' },
        { label: t.nav_blog, path: '/blog' },
    ];

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 28px', position: 'relative' }}>

            {/* Toujours monté (pas seulement quand le menu est ouvert) pour que .wlink
                soit disponible partout ailleurs sur la page — ex. Home.js. */}
            <style>{`
                @keyframes winstonMenuIn { from { opacity: 0; } to { opacity: 1; } }

                .wlink {
                    position: relative;
                    display: inline-block;
                    cursor: pointer;
                    opacity: 0.88;
                    transition: opacity .25s ease;
                }
                .wlink:hover { opacity: 1; }
                .wlink::after {
                    content: '';
                    position: absolute;
                    left: 0; right: 0; bottom: -3px;
                    height: 1px;
                    background: currentColor;
                    transform: scaleX(0);
                    transform-origin: left;
                    transition: transform .35s cubic-bezier(.4,0,.2,1);
                }
                .wlink:hover::after { transform: scaleX(1); }

                .wnav-item { transition: color .25s ease; }
                .wnav-item:hover { color: ${OV_TEXT}; }
                .wnav-item:hover .wnav-index { color: ${OV_MUTED}; }
            `}</style>

            <div onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <img
                    src="/winston-smiley-white.svg"
                    alt="winston.ie"
                    style={{ height: '48px', width: 'auto', display: 'block', filter: isLight ? 'invert(1)' : 'none' }}
                />
            </div>

            <button
                onClick={() => setMenuOpen(true)}
                aria-label={lang === 'fr' ? 'Ouvrir le menu' : 'Open menu'}
                className="wlink"
                style={{
                    background: 'none', border: 'none', padding: 0,
                    fontFamily: UI_FONT, fontWeight: 300,
                    fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                    lineHeight: 1, color,
                }}
            >
                Menu
            </button>

            {menuOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: OV_BG, display: 'flex', flexDirection: 'column', overflowY: 'auto', animation: 'winstonMenuIn .3s ease' }}>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 28px' }}>
                        <div onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <img src="/winston-smiley-white.svg" alt="winston.ie" style={{ height: '48px', width: 'auto', display: 'block' }} />
                        </div>
                        <button
                            onClick={closeMenu}
                            aria-label={lang === 'fr' ? 'Fermer le menu' : 'Close menu'}
                            className="wlink"
                            style={{
                                background: 'none', border: 'none', padding: 0,
                                fontFamily: UI_FONT, fontWeight: 300,
                                fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                                lineHeight: 1, color: OV_TEXT,
                            }}
                        >
                            {lang === 'fr' ? 'Fermer' : 'Close'}
                        </button>
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 28px 48px', maxWidth: '720px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                        {isAdminPage ? (
                            <button
                                onClick={() => { navigate('/'); closeMenu(); }}
                                className="wlink"
                                style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, fontSize: '16px', fontWeight: 500, color: OV_MUTED, fontFamily: UI_FONT }}
                            >
                                🏠 {t.nav_home}
                            </button>
                        ) : (
                            <>
                                <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '48px' }}>
                                    {navLinks.map((link, i) => (
                                        <div
                                            key={link.path}
                                            className="wnav-item"
                                            onClick={() => { navigate(link.path); closeMenu(); }}
                                            style={{ display: 'flex', alignItems: 'baseline', gap: '18px', color: OV_MUTED, cursor: 'pointer', padding: '6px 0' }}
                                        >
                                            <span className="wnav-index" style={{ fontFamily: UI_FONT, fontWeight: 300, fontSize: '12px', color: OV_MUTED_2 }}>
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            <span className="wlink" style={{ fontFamily: UI_FONT, fontWeight: 300, fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em' }}>
                                                {link.label}
                                            </span>
                                        </div>
                                    ))}
                                </nav>

                                <div style={{ borderTop: `0.5px solid ${OV_BORDER}`, paddingTop: '24px' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            autoComplete="off"
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            placeholder={t.search_placeholder}
                                            style={{
                                                width: '100%',
                                                background: 'transparent',
                                                border: 'none',
                                                padding: '0 28px 0 0',
                                                fontSize: 'clamp(16px, 2vw, 19px)',
                                                fontWeight: 500,
                                                letterSpacing: '-0.01em',
                                                color: OV_TEXT,
                                                outline: 'none',
                                                fontFamily: UI_FONT,
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={OV_MUTED} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                                            style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                            <circle cx="11" cy="11" r="7" />
                                            <path d="m21 21-4.3-4.3" />
                                        </svg>
                                    </div>
                                    {searchQuery.trim() && (
                                        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column' }}>
                                            {searchResults.length === 0 ? (
                                                <p style={{ fontSize: '12px', fontFamily: UI_FONT, fontWeight: 500, color: OV_MUTED, margin: 0 }}>{t.no_creators_found}</p>
                                            ) : (
                                                searchResults.map((u, i) => {
                                                    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                                                    return (
                                                        <div
                                                            key={u.username}
                                                            className="wlink"
                                                            onClick={() => { navigate(`/creator/${u.username}`); closeMenu(); }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                                padding: '10px 0',
                                                            }}
                                                        >
                                                            <div style={{ width: '32px', height: '32px', overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg, #444, #222)' }}>
                                                                {u.avatar_url && <img src={u.avatar_url} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                                            </div>
                                                            <div>
                                                                <p style={{ fontSize: '14px', fontFamily: UI_FONT, fontWeight: 500, color: OV_TEXT, margin: 0 }}>{fullName}</p>
                                                                <p style={{ fontSize: '11px', fontFamily: UI_FONT, fontWeight: 500, color: OV_MUTED, margin: '2px 0 0' }}>@{u.username}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginTop: '28px' }}>
                                    <button
                                        onClick={cycleLang}
                                        className="wlink"
                                        style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: OV_MUTED, fontFamily: UI_FONT }}
                                    >
                                        {lang}
                                    </button>

                                    {authUser ? (
                                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                            {authUser.role?.toLowerCase() === 'admin' && (
                                                <button onClick={() => { navigate('/admin/manage'); closeMenu(); }} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: OV_MUTED, fontFamily: UI_FONT }}>
                                                    {lang === 'fr' ? 'Gestion' : 'Management'}
                                                </button>
                                            )}
                                            <button onClick={() => { navigate('/admin/dashboard'); closeMenu(); }} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: OV_MUTED, fontFamily: UI_FONT }}>
                                                {lang === 'fr' ? 'Tableau de bord' : 'Dashboard'}
                                            </button>
                                            <button onClick={() => { goToSettings(); }} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: OV_MUTED, fontFamily: UI_FONT }}>
                                                {lang === 'fr' ? 'Paramètres' : 'Settings'}
                                            </button>
                                            <button onClick={handleLogout} className="wlink" style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: '#d98077', fontFamily: UI_FONT }}>
                                                {lang === 'fr' ? 'Se déconnecter' : 'Log out'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { openLoginModal(); closeMenu(); }}
                                            className="wlink"
                                            style={{ background: 'none', border: 'none', padding: 0, fontSize: '15px', fontWeight: 500, color: OV_MUTED, display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: UI_FONT }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="8" r="4" />
                                                <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                                            </svg>
                                            {lang === 'fr' ? 'Se connecter' : 'Log in'}
                                        </button>
                                    )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Navbar;